import { useState } from 'react';
import PropTypes from 'prop-types';
import { ClipboardCheck, Users, Package, Truck, MapPin } from 'lucide-react';

const FormatDetail = ({ label, description, length, format, type = 'text' }) => (
  <div className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <h3 className="text-[13px] font-bold uppercase tracking-[0.1em] text-[#0d0d0d]">{label}</h3>
        <span className="text-[10px] font-extrabold uppercase tracking-widest bg-[var(--theme-primary-soft)] text-[var(--theme-primary)] px-2 py-0.5 rounded-md">{type}</span>
      </div>
      {length && <span className="text-[10px] font-bold bg-gray-100 text-slate-500 px-2 py-1 rounded-md">{length} Char Max</span>}
    </div>
    <p className="text-[12px] text-slate-500 mb-3">{description}</p>
    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-1">Expected Format</p>
      <p className="text-[13px] font-mono font-medium text-[var(--theme-primary)]">{format}</p>
    </div>
  </div>
);

FormatDetail.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  length: PropTypes.string,
  format: PropTypes.string.isRequired,
  type: PropTypes.string,
};

const SectionTitle = ({ title }) => (
  <div className="col-span-full mb-2 mt-4 first:mt-0">
    <h3 className="text-sm font-bold text-[#0d0d0d] uppercase tracking-[0.12em] pb-2 border-b border-[var(--theme-tertiary)]">
      {title}
    </h3>
  </div>
);

SectionTitle.propTypes = {
  title: PropTypes.string.isRequired,
};

const TABS = [
  { id: 'products', label: 'Products & Categories', icon: Package },
  { id: 'users', label: 'Staff & Users', icon: Users },
  { id: 'branches', label: 'Branches & Warehouses', icon: MapPin },
  { id: 'suppliers', label: 'Suppliers', icon: Truck },
];

export const OrgValidationSettingsTab = () => {
  const [activeTab, setActiveTab] = useState('products');

  return (
    <div className="w-full flex flex-col gap-0 bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm">

      {/* Header */}
      <div className="p-6 border-b border-[var(--theme-tertiary)] bg-gray-50/50 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[var(--theme-primary-soft)] text-[var(--theme-primary)] flex items-center justify-center shrink-0">
          <ClipboardCheck size={20} />
        </div>
        <div>
          <h2 className="text-base font-extrabold text-[#0d0d0d] tracking-tight">Validation Format Details</h2>
          <p className="text-[13px] font-medium text-slate-500 mt-0.5">
            Reference guide for the exact input formats and rules required across the organization modules.
          </p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="px-6 flex items-center gap-6 border-b border-[var(--theme-tertiary)] bg-white overflow-x-auto custom-scrollbar">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`py-4 flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.1em] border-b-2 -mb-px transition-colors whitespace-nowrap ${
              activeTab === id
                ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                : 'border-transparent text-slate-400 hover:text-[#0d0d0d] hover:border-slate-300'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6 bg-gray-50/30">

        {/* ── Products & Categories Tab ── */}
        {activeTab === 'products' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 gap-4">
            <SectionTitle title="Master Categories" />
            <FormatDetail
              label="Category Name" type="text"
              description="The primary name of the product or service category."
              length="150"
              format="Auto-capitalizes each word. Alphanumeric with spaces."
            />
            <FormatDetail
              label="Description" type="textarea"
              description="A brief summary of what the category contains."
              length="500"
              format="Free-form text area. Allows standard punctuation."
            />

            <SectionTitle title="Product Details" />
            <FormatDetail
              label="Product Name" type="text"
              description="The full name of the product as seen on receipts and invoices."
              length="255"
              format="Alphanumeric. Allows numbers (e.g. iPhone 15, 250g)."
            />
            <FormatDetail
              label="SKU" type="text"
              description="Stock Keeping Unit / Barcode number."
              length="100"
              format="Uppercase alphanumeric only. No spaces. Automatically trims spaces."
            />
            <FormatDetail
              label="HSN / SAC Code" type="text"
              description="Harmonized System Nomenclature code for GST filing."
              length="8"
              format="Numbers only. Must be 4, 6, or 8 digits."
            />

            <SectionTitle title="Pricing & Inventory" />
            <FormatDetail
              label="Base Price" type="number"
              description="Selling price before any tax is applied."
              length={null}
              format="Positive numerical value. Allows up to 2 decimal places."
            />
            <FormatDetail
              label="MRP" type="number"
              description="Maximum Retail Price printed on the package."
              length={null}
              format="Positive numerical value. Must be >= Base Price."
            />
            <FormatDetail
              label="Reorder Level" type="number"
              description="Low stock warning threshold."
              length={null}
              format="Positive integer. No decimals."
            />
          </div>
        )}

        {/* ── Staff & Users Tab ── */}
        {activeTab === 'users' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 gap-4">
            <SectionTitle title="Personal Information" />
            <FormatDetail
              label="First Name" type="text"
              description="User's given name."
              length="100"
              format="Auto-capitalizes. Letters & spaces only."
            />
            <FormatDetail
              label="Middle Name" type="text"
              description="User's middle name (optional)."
              length="100"
              format="Auto-capitalizes. Letters & spaces only."
            />
            <FormatDetail
              label="Last Name" type="text"
              description="User's surname."
              length="100"
              format="Auto-capitalizes. Letters & spaces only."
            />
            <FormatDetail
              label="Gender" type="select"
              description="User's gender identity."
              length={null}
              format="Dropdown: Male, Female, Other."
            />
            <FormatDetail
              label="Date of Birth" type="date"
              description="Used for age calculation."
              length={null}
              format="Standard YYYY-MM-DD picker."
            />
            <FormatDetail
              label="Age" type="number"
              description="Age in years."
              length={null}
              format="Auto-calculated from DOB or manual integer input."
            />

            <SectionTitle title="Contact & Location" />
            <FormatDetail
              label="Mobile Number" type="tel"
              description="Primary mobile phone."
              length="15"
              format="Only digits, spaces, hyphens, and + sign allowed."
            />
            <FormatDetail
              label="Alternate Mobile" type="tel"
              description="Backup phone number."
              length="15"
              format="Only digits, spaces, hyphens, and + sign allowed."
            />
            <FormatDetail
              label="Email Address" type="email"
              description="Login email and communication."
              length="255"
              format="Must contain '@' and a valid domain."
            />
            <FormatDetail
              label="Full Address" type="text"
              description="Street address and area."
              length="500"
              format="Alphanumeric. Basic punctuation allowed."
            />
            <FormatDetail
              label="State & District" type="text"
              description="Regional location."
              length="100"
              format="Free-form text input. Auto-capitalizes."
            />
            <FormatDetail
              label="Pincode" type="text"
              description="Postal code."
              length="10"
              format="Alphanumeric (to support international formats)."
            />
          </div>
        )}

        {/* ── Branches & Warehouses Tab ── */}
        {activeTab === 'branches' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 gap-4">
            <SectionTitle title="Location Identity" />
            <FormatDetail
              label="Branch Name" type="text"
              description="The display name of the branch or warehouse."
              length="200"
              format="Auto-capitalizes. Alphanumeric and spaces."
            />
            <FormatDetail
              label="Code" type="text"
              description="Internal short code for the location."
              length="20"
              format="Uppercase alphanumeric without spaces (e.g. BR001)."
            />

            <SectionTitle title="Contact Details" />
            <FormatDetail
              label="Manager Phone" type="tel"
              description="Contact number for the location manager."
              length="15"
              format="Numbers and basic symbols (+, -)."
            />
            <FormatDetail
              label="Manager Email" type="email"
              description="Official email for this location."
              length="255"
              format="Valid email structure."
            />

            <SectionTitle title="Address" />
            <FormatDetail
              label="Full Address" type="textarea"
              description="Complete physical address for shipments."
              length="500"
              format="Free-form text. All standard characters allowed."
            />
          </div>
        )}

        {/* ── Suppliers Tab ── */}
        {activeTab === 'suppliers' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 min-[1920px]:grid-cols-4 gap-4">
            <SectionTitle title="Supplier Details" />
            <FormatDetail
              label="Supplier Name" type="text"
              description="Official registered name of the vendor or supplier."
              length="255"
              format="Auto-capitalizes. Alphanumeric and spaces."
            />
            <FormatDetail
              label="GST Number" type="text"
              description="Supplier's GSTIN for B2B billing and ITC claims."
              length="15"
              format="15 character alphanumeric. Exactly matches standard GST format (e.g. 33AAAAA1111A1Z1)."
            />
            <FormatDetail
              label="PAN Number" type="text"
              description="Supplier's PAN (extracted from GST or entered manually)."
              length="10"
              format="10 character uppercase alphanumeric (e.g. ABCDE1234F)."
            />

            <SectionTitle title="Contact Information" />
            <FormatDetail
              label="Contact Person" type="text"
              description="Name of the sales rep or primary contact."
              length="150"
              format="Auto-capitalizes. Letters and spaces."
            />
            <FormatDetail
              label="Phone Number" type="tel"
              description="Primary contact number."
              length="15"
              format="Only digits, spaces, hyphens, and + sign allowed."
            />
            <FormatDetail
              label="Email" type="email"
              description="Email for purchase orders."
              length="255"
              format="Valid email structure."
            />
          </div>
        )}

      </div>
    </div>
  );
};
